import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../api';
import { onCommandDone } from '../api/events';
import type { DeskItem } from '../v3/types';

/**
 * Счётчики разделов в сайдбаре (замечание Ромы, этап 15; смысл пересмотрен на этапе 17).
 *
 * Источник один — тот же `home.tasks`, что и «Сегодня»; дела раскладываются по разделам
 * по началу адреса действия (`action.to`). Кэш модульный: сайдбар и экраны читают одно
 * состояние, опрос раз в 60 с, плюс при смене маршрута и после любой команды.
 *
 * Число в капсуле — сколько дел ждут действия менеджера (`waiting = false`); ожидания
 * клиента («Ждём ответа по КП», «Ждём предоплату», «Ждём подписания») в счётчик не входят.
 * Точка — в разделе есть дело, пришедшее с сайта или из кабинета (`origin = 'client'`),
 * которое менеджер ещё не открывал. «Открыл» — побывал на странице объекта, куда ведёт дело
 * (заказ, заявка, карточка контрагента); отметку ставит due.tsx сразу при открытии страницы.
 * «Видел» хранится в localStorage по ключу дела (`wg.erp.tasks.seen.v1`, без сессий).
 */
export const BADGE_SECTIONS = ['/inquiries', '/orders', '/purchases', '/messages', '/qc', '/stock', '/counterparties', '/finance'] as const;
export type BadgeSection = (typeof BADGE_SECTIONS)[number];

export interface BadgeState {
  /** дел, ждущих действия менеджера, по разделам (только разделы с делами) */
  counts: Partial<Record<BadgeSection, number>>;
  /** сами дела — страницы объектов подсвечивают блоки, в которые они ведут (этап 16, due.tsx) */
  items: DeskItem[];
  /** ключи дел, которые менеджер уже открывал */
  seenTasks: ReadonlySet<string>;
  /** когда счётчики получены; null — ещё не загружались */
  loadedAt: number | null;
}

export const TASKS_SEEN_KEY = 'wg.erp.tasks.seen.v1';
const SEEN_MAX = 500;
const POLL_MS = 60_000;
const AFTER_COMMAND_MS = 1_500;

/** Раздел по адресу: `/orders/12?hl=…` → `/orders`, `/inquiries?sel=3` → `/inquiries`. */
export function sectionOf(path: string): BadgeSection | null {
  const p = path.split('?')[0] ?? '';
  return BADGE_SECTIONS.find((s) => p === s || p.startsWith(`${s}/`)) ?? null;
}

/** Сколько дел ждут действия менеджера в каждом разделе; ожидания клиента (`waiting`) не считаются. */
export function countBySection(items: Array<Pick<DeskItem, 'action'> & Partial<Pick<DeskItem, 'waiting'>>>): BadgeState['counts'] {
  const out: BadgeState['counts'] = {};
  for (const it of items) {
    if (it.waiting) continue;
    const s = sectionOf(it.action?.to ?? '');
    if (s) out[s] = (out[s] ?? 0) + 1;
  }
  return out;
}

/** В разделе есть дело от клиента (сайт, кабинет), которое ещё не открывали — точка в сайдбаре. */
export function hasUnseenClient(section: BadgeSection, s: Pick<BadgeState, 'items' | 'seenTasks'>): boolean {
  return s.items.some((it) => it.origin === 'client' && !s.seenTasks.has(it.key) && sectionOf(it.action?.to ?? '') === section);
}

export function readSeenTasks(): Set<string> {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(TASKS_SEEN_KEY) ?? '[]');
    return new Set(Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
  } catch { return new Set(); }
}

let state: BadgeState = { counts: {}, items: [], seenTasks: readSeenTasks(), loadedAt: null };
const listeners = new Set<() => void>();
let inflight: Promise<void> | null = null;
let timer: number | null = null;
let afterCommand: number | null = null;
let offCommand: (() => void) | null = null;

function emit(): void { listeners.forEach((fn) => fn()); }

export function getBadges(): BadgeState { return state; }

/** Перечитать счётчики; параллельные вызовы схлопываются в один запрос. Ошибки глушатся — сайдбар не ломает оболочку. */
export function refreshBadges(): Promise<void> {
  if (inflight) return inflight;
  inflight = api.query<{ items?: DeskItem[] }>('home.tasks', {})
    .then((r) => { const items = r.data?.items ?? []; state = { ...state, counts: countBySection(items), items, loadedAt: Date.now() }; emit(); })
    .catch(() => undefined)
    .finally(() => { inflight = null; });
  return inflight;
}

/** Менеджер открыл страницу объекта: его дела становятся «виденными», точка в сайдбаре гаснет, чип «новое» больше не покажется. */
export function markTasksSeen(keys: string[]): void {
  const fresh = keys.filter((k) => !state.seenTasks.has(k));
  if (!fresh.length) return;
  const all = [...state.seenTasks, ...fresh].slice(-SEEN_MAX);
  state = { ...state, seenTasks: new Set(all) };
  try { localStorage.setItem(TASKS_SEEN_KEY, JSON.stringify(all)); } catch { /* приватный режим — «новое» покажется ещё раз после перезагрузки */ }
  emit();
}

/** Только для тестов и выхода из системы: сбросить кэш (localStorage не трогаем — это не сессия). */
export function resetBadges(): void { state = { counts: {}, items: [], seenTasks: readSeenTasks(), loadedAt: null }; emit(); }

function start(): void {
  void refreshBadges();
  timer = window.setInterval(() => void refreshBadges(), POLL_MS);
  offCommand = onCommandDone(() => {
    if (afterCommand !== null) window.clearTimeout(afterCommand);
    afterCommand = window.setTimeout(() => { afterCommand = null; void refreshBadges(); }, AFTER_COMMAND_MS);
  });
}
function stop(): void {
  if (timer !== null) { window.clearInterval(timer); timer = null; }
  if (afterCommand !== null) { window.clearTimeout(afterCommand); afterCommand = null; }
  offCommand?.(); offCommand = null;
}
function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  if (listeners.size === 1) start();
  return () => { listeners.delete(fn); if (listeners.size === 0) stop(); };
}

/**
 * Состояние счётчиков для компонента. Пока есть хотя бы один подписчик, идёт опрос;
 * смена маршрута перечитывает счётчики. «Видел» отмечают страницы объектов (due.tsx), не разделы.
 */
export function useBadges(): BadgeState {
  const [s, setS] = useState<BadgeState>(state);
  const { pathname } = useLocation();
  useEffect(() => subscribe(() => setS(state)), []);
  useEffect(() => { void refreshBadges(); }, [pathname]);
  return s;
}
